import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, KeyboardEvent } from "react";
import { isNativeApp } from './platform';
import { saveNativeAlertPreferences, watchNativeAlerts } from './nativeAlerts';
import {
  connectReactions,
  fetchAppConfig,
  fetchConditions,
  savePushSubscription,
  urlBase64ToArrayBuffer
} from "./api";
import { initializeGoogleAnalytics, trackAnalyticsEvent, trackPageView } from "./analytics";
import { alertLocationFromCoordinates, unavailableAlertLocation } from "../shared/alertLocation";
import { DEFAULT_ALERT_PREFERENCES, normalizeAlertPreferences, preferenceForWeatherAlert } from "../shared/alertPreferences";
import { REACTIONS, createReaction, isShootingStarAvailable } from "../shared/reactions";
import { formatInStoneHarbor } from "../shared/stoneHarbor";
import { tideKindLabel } from "../shared/tides";
import type { AlertLocation, AlertPreferenceKey, AlertPreferences, BeachConditions, DailyForecast, ForecastPeriod, LiveReaction, ReactionKind, TideEvent, WeatherScene } from "../shared/types";

type Tab = "beach" | "tides" | "marine" | "forecast" | "alerts";
type LegalTopic = "disclaimer" | "terms" | "privacy" | "credits";

const MAX_VISIBLE_REACTIONS = 12;
const MAX_QUEUED_REACTIONS = 12;
const REACTION_STAGGER_MS = 220;
const DEFAULT_SHOOTING_STAR = { x: "-90px", y: "48px", dx: 650, dy: 210 };
const ALERT_PREF_STORAGE_KEY = "stone-harbor-alert-preferences";
const ALERT_LOCATION_STORAGE_KEY = "stone-harbor-alert-location";
const TABS: Array<{ id: Tab; label: string }> = [
  { id: "beach", label: "Beach" },
  { id: "tides", label: "Tides" },
  { id: "marine", label: "Marine" },
  { id: "forecast", label: "Forecast" },
  { id: "alerts", label: "Alerts" }
];

const ALERT_OPTIONS: Array<{ key: AlertPreferenceKey; title: string; description: string }> = [
  { key: "greenheads", title: "Greenhead flies", description: "Bay-side wind alerts when conditions can carry greenheads toward the beach." },
  { key: "weather", title: "Weather watches and warnings", description: "National Weather Service warnings and watches for Stone Harbor." },
  { key: "lightning", title: "Lightning nearby", description: "Lightning warning pushes when live detection finds strikes close to Stone Harbor." },
  { key: "marine", title: "Marine and surf", description: "Small craft, rip current, high surf, coastal flood, and beach hazard alerts." }
];

export function App() {
  const [tab, setTab] = useState<Tab>("beach");
  const [conditions, setConditions] = useState<BeachConditions | null>(null);
  const [loading, setLoading] = useState(true);
  const [conditionsLoadFailed, setConditionsLoadFailed] = useState(false);
  const [status, setStatus] = useState("Loading Stone Harbor conditions");
  const [reactions, setReactions] = useState<LiveReaction[]>([]);
  const [pushMessage, setPushMessage] = useState("Push alerts available after setup");
  const [alertPreferences, setAlertPreferences] = useState<AlertPreferences>(() => readAlertPreferences());
  const [alertOverlayOpen, setAlertOverlayOpen] = useState(false);
  const [joySheetOpen, setJoySheetOpen] = useState(false);
  const [legalTopic, setLegalTopic] = useState<LegalTopic | null>(null);
  const [analyticsReady, setAnalyticsReady] = useState(false);
  const socketRef = useRef<ReturnType<typeof connectReactions> | null>(null);
  const happySunTimerRef = useRef<number | null>(null);
  const playfulMoonTimerRef = useRef<number | null>(null);
  const localReactionEchoRef = useRef<Array<{ kind: ReactionKind; at: number }>>([]);
  const shootingStarAvailableRef = useRef(false);
  const reactionQueueRef = useRef<ReactionKind[]>([]);
  const reactionQueueTimerRef = useRef<number | null>(null);
  const nextReactionReleaseAtRef = useRef(0);
  const happySunVisibleRef = useRef(false);
  const playfulMoonVisibleRef = useRef(false);
  const [happySun, setHappySun] = useState(false);
  const [playfulMoon, setPlayfulMoon] = useState(false);

  useEffect(() => {
    if (!conditions) return;
    const isDaySun = conditions.scene.timeOfDay === "day";
    const isMoonVisible = ["night", "dawn", "dusk"].includes(conditions.scene.timeOfDay);
    const shootingStarAvailable = isShootingStarAvailable(conditions.scene.timeOfDay);
    happySunVisibleRef.current = isDaySun;
    playfulMoonVisibleRef.current = isMoonVisible;
    shootingStarAvailableRef.current = shootingStarAvailable;
    if (!isDaySun && happySunTimerRef.current) {
      window.clearTimeout(happySunTimerRef.current);
      happySunTimerRef.current = null;
      setHappySun(false);
    }
    if (!isMoonVisible && playfulMoonTimerRef.current) {
      window.clearTimeout(playfulMoonTimerRef.current);
      playfulMoonTimerRef.current = null;
      setPlayfulMoon(false);
    }
    if (!shootingStarAvailable) {
      setReactions((current) => current.some((reaction) => reaction.kind === "shootingStar")
        ? current.filter((reaction) => reaction.kind !== "shootingStar")
        : current);
    }
  }, [conditions?.scene.timeOfDay]);

  useEffect(() => {
    let cancelled = false;
    const loadConditions = (quiet = false) => {
      if (!quiet) setLoading(true);
      fetchConditions()
        .then((data) => {
          if (cancelled) return;
          setConditions(data);
          setConditionsLoadFailed(false);
          const tideAvailability = getTideAvailability(data.tide);
          const localTideModelActive = data.tide.source === "local-harmonic";
          setStatus(tideAvailability === "stale"
            ? "Live beach conditions updated; tides are showing the last available update"
            : tideAvailability === "unavailable"
              ? "Live beach conditions updated; tide predictions are temporarily unavailable"
              : localTideModelActive
                ? "Live beach conditions updated; tides are using the automatic backup model"
                : "Live beach conditions updated");
        })
        .catch((error) => {
          if (cancelled) return;
          setConditionsLoadFailed(true);
          setStatus(`Live conditions are temporarily unavailable: ${String(error.message || error)}`);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    };
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") loadConditions(true);
    };

    loadConditions();
    const interval = window.setInterval(() => loadConditions(true), 5 * 60 * 1000);
    document.addEventListener("visibilitychange", refreshWhenVisible);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, []);

  useEffect(() => {
    socketRef.current = connectReactions({
      onReaction: addReaction,
      onHello: (items) => {
        const sunIsVisible = happySunVisibleRef.current;
        const moonIsVisible = playfulMoonVisibleRef.current;
        const shootingStarIsAvailable = shootingStarAvailableRef.current;
        if (sunIsVisible && items.some((reaction) => reaction.kind === "sun")) {
          triggerHappySun();
        }
        if (moonIsVisible && items.some((reaction) => reaction.kind === "moon")) {
          triggerPlayfulMoon();
        }
        setReactions(normalizeReactions(items.filter((reaction) => {
          if (reaction.kind === "sun") return sunIsVisible;
          if (reaction.kind === "shootingStar") return shootingStarIsAvailable;
          return reaction.kind !== "moon";
        })));
      },
      onStatus: setStatus
    });
    return () => socketRef.current?.close();
  }, []);

  useEffect(() => {
    if (!isNativeApp() && "serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").catch(() => undefined);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchAppConfig()
      .then((config) => {
        if (cancelled) return;
        const ready = initializeGoogleAnalytics(config.analytics);
        if (ready) {
          trackPageView(window.location.pathname || "/", document.title);
          setAnalyticsReady(true);
        }
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!analyticsReady) return;
    trackAnalyticsEvent("section_view", { section: tab });
  }, [analyticsReady, tab]);

  useEffect(() => {
    ["/reactions/red-biplane.png", "/reactions/dolphin.png"].forEach((src) => {
      const image = new Image();
      image.src = src;
    });
  }, []);

  useEffect(() => {
    if (isNativeApp()) {
      let closed = false;
      let stop: (() => void) | undefined;
      watchNativeAlerts(() => setTab('alerts')).then(cleanup => { if (closed) cleanup(); else stop = cleanup; }).catch(() => undefined);
      return () => { closed = true; stop?.(); };
    }
    refreshExistingPushLocation().catch(() => undefined);
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") refreshExistingPushLocation().catch(() => undefined);
    };
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => document.removeEventListener("visibilitychange", refreshWhenVisible);
  }, []);

  useEffect(() => {
    return () => {
      if (happySunTimerRef.current) {
        window.clearTimeout(happySunTimerRef.current);
      }
      if (playfulMoonTimerRef.current) {
        window.clearTimeout(playfulMoonTimerRef.current);
      }
      if (reactionQueueTimerRef.current) {
        window.clearTimeout(reactionQueueTimerRef.current);
      }
    };
  }, []);

  const headline = useMemo(() => {
    if (!conditions) return "Checking live beach conditions.";
    const availability = getTideAvailability(conditions.tide);
    const tidePhrase = availability === "stale"
      ? "delayed tide update"
      : conditions.tide.state === "rising" || conditions.tide.state === "falling"
        ? `${tideStateLabel(conditions.tide.state).toLowerCase()} tide`
        : "tide update";
    const details = availability === "unavailable"
      ? [sentenceCase(conditions.summary)]
      : [sentenceCase(tidePhrase), conditions.summary.toLowerCase()];
    if (shouldShowGreenheadInHero(conditions)) {
      details.push(`${conditions.greenhead.label.toLowerCase()} greenhead risk`);
    }
    return `${details.join(", ")}.`;
  }, [conditions]);

  function addReaction(reaction: LiveReaction) {
    if (reaction.kind === "shootingStar" && !shootingStarAvailableRef.current) return;
    const now = Date.now();
    const recentEchoes = localReactionEchoRef.current.filter((echo) => now - echo.at < 1500);
    const echoIndex = recentEchoes.findIndex((echo) => echo.kind === reaction.kind);
    if (echoIndex >= 0) {
      recentEchoes.splice(echoIndex, 1);
      localReactionEchoRef.current = recentEchoes;
      return;
    }
    localReactionEchoRef.current = recentEchoes;
    if (reaction.kind === "sun") {
      if (happySunVisibleRef.current) {
        triggerHappySun();
        setReactions((current) => normalizeReactions([...current, reaction]));
      } else {
        setStatus("The sun is tucked away until daylight.");
      }
      return;
    }
    if (reaction.kind === "moon") {
      if (playfulMoonVisibleRef.current) {
        triggerPlayfulMoon();
      } else {
        setStatus("The moon will wake up after sunset.");
      }
      return;
    }
    setReactions((current) => normalizeReactions([...current, reaction]));
  }

  function triggerHappySun() {
    if (happySunTimerRef.current) {
      window.clearTimeout(happySunTimerRef.current);
    }
    setHappySun(true);
    happySunTimerRef.current = window.setTimeout(() => setHappySun(false), 30000);
  }

  function triggerPlayfulMoon() {
    if (playfulMoonTimerRef.current) {
      window.clearTimeout(playfulMoonTimerRef.current);
    }
    setPlayfulMoon(true);
    playfulMoonTimerRef.current = window.setTimeout(() => setPlayfulMoon(false), 30000);
  }

  function sendReaction(kind: ReactionKind) {
    if (kind === "shootingStar" && !shootingStarAvailableRef.current) {
      setStatus("Shooting stars come out at night in Stone Harbor.");
      return;
    }
    if (reactionQueueRef.current.length >= MAX_QUEUED_REACTIONS) {
      reactionQueueRef.current.shift();
    }
    reactionQueueRef.current.push(kind);
    scheduleQueuedReaction();
  }

  function scheduleQueuedReaction() {
    if (reactionQueueTimerRef.current !== null) return;
    const delay = Math.max(0, nextReactionReleaseAtRef.current - Date.now());
    reactionQueueTimerRef.current = window.setTimeout(() => {
      reactionQueueTimerRef.current = null;
      const kind = reactionQueueRef.current.shift();
      if (kind) {
        releaseReaction(kind);
        nextReactionReleaseAtRef.current = Date.now() + REACTION_STAGGER_MS;
      }
      if (reactionQueueRef.current.length) scheduleQueuedReaction();
    }, delay);
  }

  function releaseReaction(kind: ReactionKind) {
    if (kind === "shootingStar" && !shootingStarAvailableRef.current) {
      setStatus("Shooting stars come out at night in Stone Harbor.");
      return;
    }
    if (kind === "sun" && !happySunVisibleRef.current) {
      addReaction(createReaction(kind));
      return;
    }
    if (kind === "moon" && !playfulMoonVisibleRef.current) {
      addReaction(createReaction(kind));
      return;
    }
    addReaction(createReaction(kind));
    localReactionEchoRef.current.push({ kind, at: Date.now() });
    const sent = socketRef.current?.sendReaction(kind) ?? false;
    trackAnalyticsEvent("joy_sent", { kind, shared: sent });
    if (sent) {
      return;
    }
    if (kind === "sun") {
      return;
    }
    if (kind === "moon") {
      return;
    }
  }

  async function enablePush(preferences = alertPreferences) {
    try {
      storeAlertPreferences(preferences);
      setAlertPreferences(preferences);
      trackAnalyticsEvent("alert_preferences_save", {
        selected_count: Object.values(preferences).filter(Boolean).length
      });
      if (isNativeApp()) {
        const location = await saveNativeAlertPreferences(preferences);
        setAlertOverlayOpen(false);
        setPushMessage(Object.values(preferences).some(Boolean)
          ? `${pushPreferenceMessage(preferences, false, location)}. Reopen the app to refresh your local-area check; it expires after 6 hours.`
          : 'Background alerts are turned off for this device.');
        return;
      }
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        setPushMessage("This browser does not support web push alerts");
        return;
      }
      const config = await fetchAppConfig();
      if (!config.push.configured || !config.push.publicKey) {
        setPushMessage("Push code is ready; add VAPID keys before live alerts");
        return;
      }

      const registration = await navigator.serviceWorker.ready;
      const existing = await registration.pushManager.getSubscription();
      const hasSelectedAlert = Object.values(preferences).some(Boolean);
      if (!hasSelectedAlert && !existing) {
        setAlertOverlayOpen(false);
        setPushMessage("Alert preferences saved; no push categories are selected");
        return;
      }
      const alertLocation = hasSelectedAlert ? await getCurrentAlertLocation() : readAlertLocation();
      if (alertLocation) storeAlertLocation(alertLocation);
      if (hasSelectedAlert && !alertLocation?.inArea) {
        if (existing) await savePushSubscription(existing, preferences, alertLocation);
        setAlertOverlayOpen(false);
        setPushMessage(pushPreferenceMessage(preferences, Boolean(existing), alertLocation));
        return;
      }
      const subscription = existing || await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToArrayBuffer(config.push.publicKey)
      });
      await savePushSubscription(subscription, preferences, alertLocation);
      setAlertOverlayOpen(false);
      setPushMessage(pushPreferenceMessage(preferences, Boolean(existing), alertLocation));
    } catch (error) {
      setPushMessage(`Push setup needs attention: ${String((error as Error).message || error)}`);
    }
  }

  function navigateTo(nextTab: Tab, source: string) {
    setTab(nextTab);
    trackAnalyticsEvent("section_select", { section: nextTab, source });
  }

  function openJoySheet() {
    setJoySheetOpen(true);
    trackAnalyticsEvent("joy_open");
  }

  function openAlertPreferences(source: string) {
    setAlertOverlayOpen(true);
    trackAnalyticsEvent("alert_preferences_open", { source });
  }

  function openLegalTopic(topic: LegalTopic) {
    setLegalTopic(topic);
    trackAnalyticsEvent("legal_open", { topic });
  }

  if (!conditions) {
    return <ConditionsStatusShell loading={loading} />;
  }

  const shootingStarAvailable = isShootingStarAvailable(conditions.scene.timeOfDay);

  return (
    <main className="app-shell">
      <section className="phone-frame" aria-label="Stone Harbor beach app">
        <div className="reaction-layer" data-testid="reaction-layer" aria-hidden="true">
          {reactions.map((reaction) => (
            <ReactionSprite key={reaction.id} reaction={reaction} />
          ))}
        </div>

        <header className={`hero scene-${conditions.scene.kind} tod-${conditions.scene.timeOfDay}${conditions.scene.lightning.withinSevenMiles ? " lightning-warning" : ""}`}>
          <WeatherSceneLayer scene={conditions.scene} happySun={happySun} playfulMoon={playfulMoon} />
          <div className="temperature">{conditions.temperatureF}°</div>
          <p data-testid="hero-headline">{headline}</p>
          <span className="risk-chip tide-chip">
            {nextTideChipLabel(conditions.tide)}
          </span>
          {conditions.greenhead.level !== "low" && !isGreenheadSuppressedByRain(conditions) ? <GreenheadFly level={conditions.greenhead.level} /> : null}
          {conditions.alertSummary.activeCount ? (
            <button className={`alert-chip alert-${conditions.alertSummary.highestCategory}`} onClick={() => navigateTo("alerts", "weather_alert_chip")} type="button">
              {conditions.alertSummary.activeCount} NWS alert{conditions.alertSummary.activeCount === 1 ? "" : "s"}
            </button>
          ) : null}
          {conditions.scene.lightning.withinSevenMiles ? (
            <span className="lightning-chip" role="status">
              Lightning {conditions.scene.lightning.distanceMiles ? `${conditions.scene.lightning.distanceMiles} mi` : "nearby"}
            </span>
          ) : null}
        </header>

        <div className="wave-band">
          <BoatTitle />
        </div>

        <nav className="tab-bar" aria-label="App sections">
          {TABS.map((item) => (
            <button key={item.id} className={tab === item.id ? "active" : ""} onClick={() => navigateTo(item.id, "tab_bar")} type="button">
              {item.label}
            </button>
          ))}
          <button className="joy-tab" onClick={openJoySheet} type="button" aria-label="Open beach joy tray">
            <span aria-hidden="true">✦</span>
            <strong>Joy</strong>
          </button>
        </nav>

        <section className="content">
          {loading ? <div className="card soft">Refreshing live conditions...</div> : null}
          {conditionsLoadFailed ? (
            <div className="provider-notice provider-notice-warning" role="status">
              <strong>Live conditions could not refresh</strong>
              <p>Showing the most recent update already on this screen. Try again in a moment.</p>
            </div>
          ) : null}
          {tab === "beach" ? <BeachView conditions={conditions} onNavigate={(nextTab) => navigateTo(nextTab, "beach_tile")} pushMessage={pushMessage} onEnablePush={() => openAlertPreferences("greenhead_card")} /> : null}
          {tab === "tides" ? <TidesView conditions={conditions} /> : null}
          {tab === "marine" ? <MarineView conditions={conditions} /> : null}
          {tab === "forecast" ? <ForecastView conditions={conditions} /> : null}
          {tab === "alerts" ? <AlertsView conditions={conditions} pushMessage={pushMessage} onEnablePush={() => openAlertPreferences("alerts_page")} /> : null}
        </section>

        {joySheetOpen ? <JoySheet onClose={() => setJoySheetOpen(false)} onSend={sendReaction} shootingStarAvailable={shootingStarAvailable} status={status} /> : null}

        <LegalFooter onOpen={openLegalTopic} />

        {legalTopic ? <LegalSheet topic={legalTopic} onClose={() => setLegalTopic(null)} /> : null}

        {alertOverlayOpen ? (
          <AlertPreferencesOverlay
            preferences={alertPreferences}
            pushMessage={pushMessage}
            onChange={setAlertPreferences}
            onCancel={() => setAlertOverlayOpen(false)}
            onSave={enablePush}
          />
        ) : null}
      </section>
    </main>
  );
}

function ConditionsStatusShell({ loading }: { loading: boolean }) {
  return (
    <main className="app-shell">
      <section className="phone-frame conditions-state-shell" aria-label="Stone Harbor beach app">
        <div className="conditions-state" role="status" aria-live="polite">
          <span>Stone Harbor</span>
          <h1>{loading ? "Checking live beach conditions" : "Live conditions are temporarily unavailable"}</h1>
          <p>{loading
            ? "Getting the latest weather, marine, and tide updates."
            : "We could not reach the live providers. No estimated conditions are being shown."}</p>
          {!loading ? <button className="primary-button" onClick={() => window.location.reload()} type="button">Try again</button> : null}
        </div>
      </section>
    </main>
  );
}

function GreenheadFly({ level }: { level: BeachConditions["greenhead"]["level"] }) {
  return (
    <div className={`greenhead-fly-badge fly-${level}`} data-testid="greenhead-fly" role="status" aria-label="Greenhead fly possible with bay-side wind">
      <svg viewBox="0 0 128 74" role="img" aria-hidden="true">
        <defs>
          <linearGradient id="greenheadBodyGradient" x1="27" x2="116" y1="19" y2="56" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#3f3125" />
            <stop offset="0.45" stopColor="#776243" />
            <stop offset="1" stopColor="#2b241e" />
          </linearGradient>
        </defs>
        <g className="greenhead-wings">
          <path className="greenhead-wing greenhead-wing-top" d="M16 21c22-14 52-11 76 4-27 9-55 10-84 2 1-2 4-4 8-6Z" />
          <path className="greenhead-wing greenhead-wing-bottom" d="M16 45c30-12 58-13 88-3-28 12-63 15-96 8 1-2 4-4 8-5Z" />
          <path className="greenhead-wing-vein" d="M19 27c25 1 50 1 73-2M21 49c29-4 55-7 82-7M42 24l-20 23M62 24 34 49M82 28 53 49" />
        </g>
        <path className="greenhead-leg leg-one" d="M64 45 50 68" />
        <path className="greenhead-leg leg-two" d="M82 45 91 71" />
        <path className="greenhead-leg leg-three" d="M101 40 119 58" />
        <path className="greenhead-body" d="M27 34c5-15 23-24 48-20 22 4 39 15 41 28-11 14-35 20-58 14-18-4-29-12-31-22Z" />
        <path className="greenhead-stripe stripe-one" d="M38 27c-3 9-2 18 3 25" />
        <path className="greenhead-stripe stripe-two" d="M53 21c-5 11-4 24 2 35" />
        <path className="greenhead-stripe stripe-three" d="M70 20c-5 10-5 23 1 35" />
        <path className="greenhead-head" d="M89 21c15-1 27 8 28 21-3 10-14 17-28 15-8-10-8-26 0-36Z" />
        <path className="greenhead-eye" d="M96 24c10 0 18 7 19 17-2 8-10 13-20 12-5-9-5-20 1-29Z" />
        <path className="greenhead-antenna left" d="M91 22 82 5" />
        <path className="greenhead-antenna right" d="M107 23 119 8" />
        <path className="greenhead-mouth" d="M115 42 126 39" />
      </svg>
    </div>
  );
}

function AlertPreferencesOverlay({
  preferences,
  pushMessage,
  onChange,
  onCancel,
  onSave
}: {
  preferences: AlertPreferences;
  pushMessage: string;
  onChange: (preferences: AlertPreferences) => void;
  onCancel: () => void;
  onSave: (preferences: AlertPreferences) => void;
}) {
  const selectedCount = Object.values(preferences).filter(Boolean).length;

  function togglePreference(key: AlertPreferenceKey) {
    onChange({ ...preferences, [key]: !preferences[key] });
  }

  return (
    <div className="alert-overlay" role="presentation">
      <section className="alert-dialog" role="dialog" aria-modal="true" aria-labelledby="alert-dialog-title">
        <div className="card-title">
          <h2 id="alert-dialog-title">Alert preferences</h2>
          <span>{selectedCount} selected</span>
        </div>
        <p>Choose which alerts this device should receive. Saving only updates this phone; it does not send an alert to anyone. Push alerts only activate after this phone confirms it is within 10 miles of Stone Harbor.</p>
        <div className="alert-choice-list">
          {ALERT_OPTIONS.map((option) => (
            <label className="alert-choice" key={option.key}>
              <input
                checked={preferences[option.key]}
                onChange={() => togglePreference(option.key)}
                type="checkbox"
              />
              <span>
                <strong>{option.title}</strong>
                <small>{option.description}</small>
              </span>
            </label>
          ))}
        </div>
        <div className="alert-dialog-actions">
          <button className="secondary-button" onClick={onCancel} type="button">Cancel</button>
        <button className="primary-button" onClick={() => onSave(preferences)} type="button">Activate selected alerts</button>
        </div>
        <small>{pushMessage}</small>
      </section>
    </div>
  );
}

function BoatTitle() {
  return (
    <div className="boat-title" aria-label="Stone Harbor">
      <svg viewBox="0 0 260 78" role="img" aria-hidden="true">
        <path className="boat-shadow" d="M30 61c45 7 144 7 198 0 7-1 8 6 1 8-56 10-156 10-201 0-7-2-5-9 2-8Z" />
        <path className="boat-oar boat-oar-left" d="M82 28 35 12c-6-2-9 7-3 10l47 22Z" />
        <path className="boat-oar boat-oar-right" d="M178 28 226 12c6-2 9 7 3 10l-48 22Z" />
        <path className="boat-hull" d="M24 24c44 4 164 4 212 0-3 27-22 43-61 48H86c-35-4-56-19-62-48Z" />
        <path className="boat-rim" d="M28 24c43 5 160 5 205 0" />
        <path className="boat-seat" d="M80 25h100v11H80Z" />
        <path className="boat-lines" d="M51 37h157M58 47h141M69 57h118" />
        <text x="130" y="54" textAnchor="middle">STONE HARBOR</text>
      </svg>
    </div>
  );
}

function WeatherSceneLayer({ scene, happySun, playfulMoon }: { scene: WeatherScene; happySun: boolean; playfulMoon: boolean }) {
  const showRain = scene.kind === "rainy" || scene.kind === "thunderstorm";
  const showSnow = scene.kind === "snowy";
  const moonLabel = `Moon phase: ${scene.moon.label}, ${scene.moon.illumination}% illuminated`;
  const moonStyle = {
    "--moon-illumination": `${scene.moon.illumination}%`
  } as CSSProperties;

  return (
    <div className={`weather-scene scene-${scene.kind} tod-${scene.timeOfDay}`} data-testid="weather-scene" aria-label={`${scene.label}, ${scene.timeOfDay}`}>
      <div className={happySun ? "sun-orb is-happy" : "sun-orb"} data-testid="happy-sun">
        <span className="sun-face" data-testid="happy-sun-face" aria-hidden="true">
          <i className="sun-eye left" />
          <i className="sun-eye right" />
          <i className="sun-shades-bridge" />
          <i className="sun-smile" />
        </span>
      </div>
      <div
        aria-label={moonLabel}
        className={`moon-orb phase-${scene.moon.name}${scene.moon.waxing ? " moon-waxing" : " moon-waning"}${playfulMoon ? " is-playful" : ""}`}
        data-illumination={scene.moon.illumination}
        data-phase={scene.moon.phase}
        data-waxing={scene.moon.waxing}
        data-testid="moon-phase"
        role="img"
        style={moonStyle}
        title={`${scene.moon.label}, ${scene.moon.illumination}% illuminated`}
      >
        <svg className="moon-phase-svg" viewBox="0 0 100 100" aria-hidden="true">
          <circle className="moon-disc-shadow" cx="50" cy="50" r="50" />
          {scene.moon.illumination > 0 ? <path className="moon-disc-light" d={moonIlluminationPath(scene.moon.illumination, scene.moon.waxing)} /> : null}
          <g className="moon-disc-texture">
            <circle cx="31" cy="34" r="5" />
            <circle cx="65" cy="31" r="3.5" />
            <circle cx="56" cy="68" r="6" />
          </g>
        </svg>
        <span className="sr-only">{scene.moon.label}</span>
        <span className="moon-face" data-testid="moon-face" aria-hidden="true">
          <i className="moon-eye left" />
          <i className="moon-eye right" />
          <i className="moon-smile" />
          <i className="moon-cheese one" />
          <i className="moon-cheese two" />
          <i className="moon-cheese three" />
        </span>
      </div>
      <span className="scene-cloud cloud-one" />
      <span className="scene-cloud cloud-two" />
      <span className="scene-cloud cloud-three" />
      {showRain ? <span className="rain-curtain" /> : null}
      {showSnow ? <span className="snow-flurry" /> : null}
      {scene.kind === "thunderstorm" ? <span className="lightning-bolt" /> : null}
    </div>
  );
}

function moonIlluminationPath(illumination: number, waxing: boolean) {
  const litFraction = Math.min(1, Math.max(0, illumination / 100));
  if (litFraction <= 0) return "";
  if (litFraction >= 0.995) return "M 50 0 A 50 50 0 1 1 49.99 0 Z";

  const rightSideLit = waxing;
  const rightSideFraction = rightSideLit ? litFraction : 1 - litFraction;
  const chordX = 50 + chordXForRightCircleFraction(rightSideFraction) * 50;
  const sweep = rightSideLit ? 1 : 0;
  return `M 50 0 A 50 50 0 0 ${sweep} 50 100 C ${chordX.toFixed(2)} 100 ${chordX.toFixed(2)} 0 50 0 Z`;
}

function chordXForRightCircleFraction(targetFraction: number) {
  const target = Math.min(1, Math.max(0, targetFraction));
  let low = -1;
  let high = 1;
  for (let index = 0; index < 28; index += 1) {
    const midpoint = (low + high) / 2;
    if (rightCircleFraction(midpoint) > target) {
      low = midpoint;
    } else {
      high = midpoint;
    }
  }
  return (low + high) / 2;
}

function rightCircleFraction(chordX: number) {
  const x = Math.min(1, Math.max(-1, chordX));
  return (Math.acos(x) - x * Math.sqrt(Math.max(0, 1 - x * x))) / Math.PI;
}

function BeachView({
  conditions,
  onNavigate,
  pushMessage,
  onEnablePush
}: {
  conditions: BeachConditions;
  onNavigate: (tab: Tab) => void;
  pushMessage: string;
  onEnablePush: () => void;
}) {
  const tideAvailability = getTideAvailability(conditions.tide);
  const tideUnavailable = tideAvailability === "unavailable";
  const localTideModelActive = conditions.tide.source === "local-harmonic";
  const tidePercent = conditions.tide.current?.percent ?? 50;
  const tideSandWidth = tideSandFillPercent(tidePercent);
  const tideMotion = tideMotionDirection(conditions.tide.state);
  const tideMeterStyle = { "--tide-sand-width": `${tideSandWidth}%` } as CSSProperties;

  return (
    <>
      <div className="metric-grid">
        <Metric label="Feels like" value={`${conditions.feelsLikeF}°`} onActivate={() => onNavigate("forecast")} targetLabel="forecast" tone="weather" />
        <Metric label="Wind" value={`${conditions.windDirection} ${conditions.windMph}`} tone="wind" />
      </div>

      {conditions.alertSummary.activeCount ? <WeatherAlertsCard conditions={conditions} /> : null}

      <div className="card sun-card tile-sun">
        <SunTime label="Sunrise" value={shortTime(conditions.sunrise)} />
        <SunTime label="Sunset" value={shortTime(conditions.sunset)} />
      </div>

      <div
        aria-label="Open tides from tide tile"
        className="card shortcut-card tile-tide"
        onClick={() => onNavigate("tides")}
        onKeyDown={activateOnKeyboard(() => onNavigate("tides"))}
        role="button"
        tabIndex={0}
      >
        <div className="card-title">
          <h2>Tide</h2>
          <span>{tidePositionBadge(conditions.tide)}</span>
        </div>
        {tideUnavailable ? (
          <div className="tide-card-message" role="status">
            <strong>Tide data is temporarily unavailable</strong>
            <span>Open Tides for provider details.</span>
          </div>
        ) : (
          <>
            <div
              aria-label="Current tide position"
              aria-valuemax={100}
              aria-valuemin={0}
              aria-valuenow={Math.round(tidePercent)}
              aria-valuetext={conditions.tide.current?.label || tideStateLabel(conditions.tide.state)}
              className="tide-bar"
              data-sand-width={`${tideSandWidth}%`}
              data-testid="tide-bar"
              data-tide-motion={tideMotion}
              role="meter"
              style={tideMeterStyle}
            />
            {conditions.tide.current ? <small className="tide-position-label">{conditions.tide.current.label}</small> : null}
            {tideAvailability === "stale"
              ? <small className="tide-delay-label">Tide feed delayed · last update {conditions.tide.lastSuccessfulAt ? shortDateTime(conditions.tide.lastSuccessfulAt) : "recently"}</small>
              : localTideModelActive
                ? <small className="tide-delay-label">Backup tide model active</small>
                : null}
            <div className="split-row">
              <span>Next high tide</span>
              <strong>{conditions.tide.nextHigh ? shortTime(conditions.tide.nextHigh.time) : "Delayed"}</strong>
            </div>
            <div className="split-row">
              <span>Next low tide</span>
              <strong>{conditions.tide.nextLow ? shortTime(conditions.tide.nextLow.time) : "Delayed"}</strong>
            </div>
          </>
        )}
      </div>

      <MarineCard conditions={conditions} compact onActivate={() => onNavigate("marine")} />

      {!isGreenheadSuppressedByRain(conditions) ? (
        <div className={`card greenhead tile-greenhead risk-${conditions.greenhead.level}`}>
          <div className="card-title">
            <h2>Greenhead fly watch</h2>
            <span>{conditions.greenhead.notify ? "Notify" : "Quiet"}</span>
          </div>
          <p>{conditions.greenhead.reason}</p>
          <button className="primary-button" onClick={onEnablePush} type="button">Activate alerts</button>
          <small>{pushMessage}</small>
        </div>
      ) : null}
    </>
  );
}

function TidesView({ conditions }: { conditions: BeachConditions }) {
  const availability = getTideAvailability(conditions.tide);
  const localTideModelActive = conditions.tide.source === "local-harmonic";
  return (
    <div className="card">
      <div className="card-title">
        <h2>Upcoming tides</h2>
        <span>{conditions.tide.stationName}</span>
      </div>
      {availability === "live" && localTideModelActive ? (
        <div className="provider-notice provider-notice-stale" role="status" aria-live="polite">
          <strong>Backup tide model active</strong>
          <p>NOAA's station feed is unavailable, so this chart is being calculated locally from Stone Harbor's published harmonic constants. For beach planning only—not navigation.</p>
          <small>Source: NOAA CO-OPS station 8535581 · calculated locally</small>
        </div>
      ) : null}
      {availability !== "live" ? (
        <div className={`provider-notice provider-notice-${availability}`} role="status" aria-live="polite">
          <strong>{availability === "stale" ? "Showing the last available tide update" : "Tide data is temporarily unavailable"}</strong>
          <p>{availability === "stale"
            ? `Live tide updates are delayed. These times were last updated ${conditions.tide.lastSuccessfulAt ? shortDateTime(conditions.tide.lastSuccessfulAt) : "recently"}.`
            : "NOAA's feed and the automatic backup model are not returning predictions right now. Weather and marine conditions remain available."}</p>
        </div>
      ) : null}
      <div className="tide-days">
        {conditions.tide.days.map((day) => (
          <section className="tide-day" key={day.date}>
            <div className="tide-day-head">
              <strong>{day.label}</strong>
              <span>{day.events.length} changes</span>
            </div>
            {day.events.map((event) => (
              <div className="tide-row" key={event.id}>
                <span>{tideKindLabel(event.kind)}</span>
                <strong>{shortTime(event.time)}</strong>
              </div>
            ))}
          </section>
        ))}
        {!conditions.tide.days.length && availability === "live" ? (
          <div className="provider-notice provider-notice-warning" role="status">
            <strong>No upcoming tide changes were returned</strong>
            <p>Please check again shortly.</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function MarineCard({ conditions, compact = false, onActivate }: { conditions: BeachConditions; compact?: boolean; onActivate?: () => void }) {
  const marine = conditions.marine;
  const shortcutProps = onActivate
    ? {
        "aria-label": "Open marine forecast from marine tile",
        className: `card shortcut-card marine-card tile-marine marine-${marine.status}`,
        onClick: onActivate,
        onKeyDown: activateOnKeyboard(onActivate),
        role: "button",
        tabIndex: 0
      }
    : {
        className: `card marine-card tile-marine marine-${marine.status}`
      };

  return (
    <div {...shortcutProps}>
      <div className="card-title">
        <h2>Marine</h2>
        <span>{sentenceCase(marine.status)}</span>
      </div>
      <p>{marine.headline}</p>
      <div className="marine-quick-grid">
        <MarinePill label="Waves" value={`${formatFeet(marine.waveHeightFt)} ft`} />
        <MarinePill label="Period" value={marine.wavePeriodSec ? `${formatNumber(marine.wavePeriodSec)} sec` : "Checking"} />
        <MarinePill label="Water" value={marine.seaSurfaceTemperatureF ? `${Math.round(marine.seaSurfaceTemperatureF)}°` : "Checking"} />
        <MarinePill label="Direction" value={marine.waveDirection || "Checking"} />
      </div>
      {!compact ? (
        <div className="marine-detail-list">
          <div className="split-row">
            <span>Swell</span>
            <strong>{marine.swellHeightFt ? `${formatFeet(marine.swellHeightFt)} ft ${marine.swellDirection || ""}` : "Checking"}</strong>
          </div>
          <div className="split-row">
            <span>Wind waves</span>
            <strong>{marine.windWaveHeightFt ? `${formatFeet(marine.windWaveHeightFt)} ft ${marine.windWaveDirection || ""}` : "Light"}</strong>
          </div>
          <small>Marine forecast: {marine.source === "open-meteo-marine" ? "Open-Meteo Marine" : "fallback mode"}.</small>
        </div>
      ) : null}
    </div>
  );
}

function MarineView({ conditions }: { conditions: BeachConditions }) {
  const marineAlerts = conditions.weatherAlerts.filter((alert) => preferenceForWeatherAlert(alert) === "marine");
  return (
    <div className="marine-panel">
      <MarineCard conditions={conditions} />
      <div className="card">
        <div className="card-title">
          <h2>Marine forecast</h2>
          <span>{conditions.marine.forecast.length} periods</span>
        </div>
        <div className="marine-forecast-list">
          {conditions.marine.forecast.map((period) => (
            <article className="marine-period" key={period.time}>
              <div>
                <strong>{period.label}</strong>
                <span>{period.waveDirection || "Atlantic"} waves</span>
              </div>
              <div>
                <b>{formatFeet(period.waveHeightFt)} ft</b>
                <small>{period.wavePeriodSec ? `${formatNumber(period.wavePeriodSec)} sec` : ""}</small>
              </div>
            </article>
          ))}
        </div>
      </div>
      <div className={`card alerts-card ${marineAlerts.length ? "alert-advisory" : ""}`}>
        <div className="card-title">
          <h2>Marine alerts</h2>
          <span>{marineAlerts.length ? `${marineAlerts.length} active` : "All clear"}</span>
        </div>
        {marineAlerts.length ? (
          <div className="alerts-list">
            {marineAlerts.slice(0, 3).map((alert) => (
              <article className="alert-item" key={alert.id}>
                <strong>{alert.event}</strong>
                <span>{alert.headline}</span>
                <small>{alert.severity} / {alert.urgency}</small>
              </article>
            ))}
          </div>
        ) : (
          <p>No active NWS marine, surf, rip current, or coastal hazard alerts for Stone Harbor right now.</p>
        )}
      </div>
    </div>
  );
}

function MarinePill({ label, value }: { label: string; value: string }) {
  return (
    <div className="marine-pill">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function ForecastView({ conditions }: { conditions: BeachConditions }) {
  const [mode, setMode] = useState<"hourly" | "daily">("hourly");

  return (
    <div className="forecast-panel">
      <div className="forecast-toggle" role="group" aria-label="Forecast type">
        <button className={mode === "hourly" ? "active" : ""} onClick={() => setMode("hourly")} type="button">
          Hourly
        </button>
        <button className={mode === "daily" ? "active" : ""} onClick={() => setMode("daily")} type="button">
          Daily
        </button>
      </div>
      <div className="forecast-list" key={mode}>
        {mode === "hourly"
          ? conditions.forecast.map((period) => (
            <ForecastCard key={`${period.time}-${period.label}`} period={period} />
          ))
          : conditions.dailyForecast.map((period) => (
            <DailyForecastCard key={`daily-${period.date}`} period={period} />
          ))}
      </div>
    </div>
  );
}

function AlertsView({ conditions, pushMessage, onEnablePush }: { conditions: BeachConditions; pushMessage: string; onEnablePush: () => void }) {
  return (
    <>
      <WeatherAlertsCard conditions={conditions} />
      <div className="card alerts-card">
        <div className="card-title">
          <h2>Alert notifications</h2>
          <span>{conditions.alertSummary.pushRecommended ? "Recommended" : "Ready"}</span>
        </div>
        <p>Use this for National Weather Service warnings, marine and surf alerts, lightning status, and bay-wind greenhead push alerts.</p>
        <button className="primary-button" onClick={onEnablePush} type="button">Activate alerts</button>
        <small>{pushMessage}</small>
      </div>
    </>
  );
}

function JoySheet({ onClose, onSend, shootingStarAvailable, status }: { onClose: () => void; onSend: (kind: ReactionKind) => void; shootingStarAvailable: boolean; status: string }) {
  useEffect(() => {
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <div className="joy-sheet-backdrop" onPointerDown={onClose}>
      <section className="joy-sheet" role="dialog" aria-modal="true" aria-labelledby="joy-sheet-title" onPointerDown={(event) => event.stopPropagation()}>
      <div className="card-title">
        <h2 id="joy-sheet-title">Send a beach moment</h2>
        <button className="icon-button" onClick={onClose} type="button" aria-label="Close beach joy tray">×</button>
      </div>
      <p>{status}</p>
      <div className="joy-sheet-note">
        <span>Live</span>
        <small>Moments drift through the beach scene for 30 seconds.</small>
      </div>
      <span id="shooting-star-availability" className="sr-only">Shooting stars are available at night in Stone Harbor.</span>
      <div className="reaction-grid">
        {REACTIONS.map((reaction) => {
          const disabled = reaction.kind === "shootingStar" && !shootingStarAvailable;
          return (
            <button
              key={reaction.kind}
              type="button"
              onClick={() => onSend(reaction.kind)}
              aria-label={`Send ${reaction.label}`}
              aria-describedby={disabled ? "shooting-star-availability" : undefined}
              disabled={disabled}
              title={disabled ? "Available at night in Stone Harbor" : undefined}
            >
              <ReactionGlyph kind={reaction.kind} glyph={reaction.glyph} />
              <span>{reaction.label}</span>
            </button>
          );
        })}
      </div>
      </section>
    </div>
  );
}

function LegalFooter({ onOpen }: { onOpen: (topic: LegalTopic) => void }) {
  return (
    <footer className="legal-footer">
      <div>
        <button onClick={() => onOpen("disclaimer")} type="button">Disclaimer</button>
        <button onClick={() => onOpen("terms")} type="button">Terms</button>
        <button onClick={() => onOpen("privacy")} type="button">Privacy</button>
        <button onClick={() => onOpen("credits")} type="button">Credits</button>
      </div>
      <small>© 2026 {isNativeApp() ? 'Cuelora LLC' : 'JCP Tech'}. All rights reserved.</small>
      <a className="cuelora-link" href="https://cuelora.com" target="_blank" rel="noopener noreferrer">cuelora.com</a>
    </footer>
  );
}

function LegalSheet({ topic, onClose }: { topic: LegalTopic; onClose: () => void }) {
  const title = {
    disclaimer: "Disclaimer",
    terms: "Terms of Use",
    privacy: "Privacy",
    credits: "Credits"
  }[topic];

  return (
    <div className="legal-sheet-backdrop">
      <section className="legal-sheet" role="dialog" aria-modal="true" aria-labelledby="legal-sheet-title">
        <div className="card-title">
          <h2 id="legal-sheet-title">{title}</h2>
          <button className="icon-button" onClick={onClose} type="button" aria-label="Close legal details">×</button>
        </div>
        {topic === "disclaimer" ? <DisclaimerContent /> : null}
        {topic === "terms" ? <TermsContent /> : null}
        {topic === "privacy" ? <PrivacyContent /> : null}
        {topic === "credits" ? <CreditsContent /> : null}
      </section>
    </div>
  );
}

function DisclaimerContent() {
  return (
    <div className="legal-copy">
      <p>This app is for quick family beach planning only. Weather, tide, marine, lightning, and greenhead information can be delayed, incomplete, or wrong.</p>
      <p>Do not rely on this app for emergency decisions, boating safety, surf safety, evacuation decisions, or severe weather protection. Check official NOAA/NWS sources, local authorities, beach patrol, lifeguards, and posted warnings.</p>
      <p>Greenhead risk is an estimate based mainly on wind direction and speed. Push notifications are best-effort and may not arrive.</p>
    </div>
  );
}

function TermsContent() {
  return (
    <div className="legal-copy">
      <p>By using this app, you agree to use it at your own risk and only as an informal planning aid.</p>
      <p>The app may change, pause, or stop working at any time. No warranty is made that the information is accurate, available, or fit for a specific purpose.</p>
      <p>This app is not affiliated with, endorsed by, or sponsored by the Borough of Stone Harbor, NOAA, the National Weather Service, Open-Meteo, Weatherbit, or Cloudflare.</p>
    </div>
  );
}

function PrivacyContent() {
  return (
    <div className="legal-copy">
      <p>Last updated October 21, 2026.</p>
        <p>Stone Harbor Beach App is published by Cuelora LLC, 611 Gittings Ave, Baltimore, MD 21212. Privacy questions or requests: <a href="mailto:admin@cuelora.com?subject=Stone%20Harbor%20privacy">admin@cuelora.com</a> or 888-900-1733.</p>
      <p>The app does not require an account. Alert choices are stored on this device and, if push notifications are enabled, the push subscription endpoint, selected alert categories, and a recent within-10-miles yes/no location check are stored only to send requested local alerts.</p>
      <p>Cloudflare processes ordinary technical request information, including IP-derived region, to serve the app and enforce regional availability. Choosing a beach reaction sends its preset type to Cloudflare; the resulting animation, random reaction identifier and timestamps are shared with other connected visitors. A small recent-reaction list is retained. Expired entries stop appearing and are replaced when another reaction is saved. Reactions do not include your name, account details or precise location.</p>
      <p>{isNativeApp() ? 'The native app does not load Google Analytics. Local alert preferences stay on this device and are removed when you clear app data or uninstall.' : 'Google Analytics is active on this app and may collect basic usage analytics such as page views, section views, device/browser details, approximate location derived from network information, and interaction events so we can understand whether the app is useful.'}</p>
      {!isNativeApp() ? <p>Google Analytics may use cookies or similar identifiers. The app does not send your alert location coordinates, push subscription endpoint, or selected alert preferences to Google Analytics.</p> : null}
      <p>{isNativeApp() ? 'If you enable background alerts, Google Firebase Cloud Messaging and Apple Push Notification service process delivery identifiers. Cloudflare stores your delivery token, chosen categories, and a dated yes/no local-area check. Exact coordinates stay on your phone. The local-area check expires after 6 hours; reopen the app to refresh it. Turning all alert categories off removes the delivery subscription. Delivery subscriptions inactive for 30 days are removed. A hashed device revocation record prevents old requests from re-enabling alerts.' : 'We do not sell personal information. Cloudflare, Google Analytics, and third-party data providers may process ordinary technical request information needed to deliver the app, analytics, and API responses.'}</p>
      {isNativeApp() ? <p>Native notification SDKs may send technical information, including SDK, app and operating-system versions and operational usage and diagnostics, to Google to maintain and improve notification services. This operational analytics is separate from Google Analytics; the native app does not display ads or use cross-app advertising tracking.</p> : null}
      <p>Cloudflare retains operational request logs, which may include IP addresses and request metadata, for up to seven days for service operation, security and troubleshooting. Choosing a Joy reaction sends its preset type to Cloudflare; a random reaction identifier and timestamps are shared with connected visitors. Reactions do not include your name or precise location.</p>
      <p>{isNativeApp() ? 'You can turn all alert categories off in the app, or remove notification and location permissions in your phone settings. We do not sell personal information.' : 'You can remove push access from your browser or phone settings at any time, and you can limit analytics with your browser privacy settings or Google Analytics opt-out tools.'}</p>
      <p><a href="https://stone-harbor-beach-day-app.com/privacy" target="_blank" rel="noreferrer">Read the full privacy policy</a></p>
    </div>
  );
}

function CreditsContent() {
  return (
    <div className="legal-copy credits-copy">
      <p>Daily forecast and official weather alerts: <a href="https://www.weather.gov/documentation/services-web-api" target="_blank" rel="noreferrer">National Weather Service API</a>.</p>
      <p>Hourly model forecast, sunrise, sunset, and weather scene data: <a href="https://open-meteo.com/" target="_blank" rel="noreferrer">Open-Meteo</a>.</p>
      <p>Marine forecast data: <a href="https://open-meteo.com/en/docs/marine-weather-api" target="_blank" rel="noreferrer">Open-Meteo Marine Weather API</a>.</p>
      <p>Tide predictions: <a href="https://tidesandcurrents.noaa.gov/" target="_blank" rel="noreferrer">NOAA Tides & Currents / CO-OPS</a>. Automatic backup calculations use the <a href="https://github.com/openwatersio/neaps" target="_blank" rel="noreferrer">Neaps tide predictor</a> with NOAA-published Stone Harbor harmonic constants.</p>
      <p>Lightning strike detection, when configured: <a href="https://www.weatherbit.io/" target="_blank" rel="noreferrer">Weatherbit</a>. Hosting and edge runtime: <a href="https://developers.cloudflare.com/workers/" target="_blank" rel="noreferrer">Cloudflare Workers</a>.</p>
      {!isNativeApp() ? <p>Usage analytics: <a href="https://analytics.google.com/" target="_blank" rel="noreferrer">Google Analytics</a> through Google tag ID G-K3EJW7LY4Q.</p> : null}
      <p>Reaction artwork includes the red biplane from <a href="https://freesvg.org/biplane-red-color" target="_blank" rel="noreferrer">FreeSVG / OpenClipart</a> and the dolphin from <a href="https://freesvg.org/dolphin-colored-clip-art" target="_blank" rel="noreferrer">FreeSVG / OpenClipart</a>.</p>
      <p>Boat title, app icon, sandcastle, beach scene, weather scene, and other reaction artwork are app-specific CSS/SVG artwork created for the Stone Harbor Beach App.</p>
      <p>Built with React, Vite, TypeScript, and Cloudflare Workers.</p>
      <p><a href="/third-party-notices.txt" target="_blank" rel="noreferrer">Third-party notices</a>.</p>
    </div>
  );
}

function WeatherAlertsCard({ conditions }: { conditions: BeachConditions }) {
  const alerts = conditions.weatherAlerts || [];

  return (
    <div className={`card alerts-card alert-${conditions.alertSummary.highestCategory}`}>
      <div className="card-title">
        <h2>Live weather alerts</h2>
        <span>{alerts.length ? `${alerts.length} active` : "All clear"}</span>
      </div>
      {alerts.length ? (
        <div className="alerts-list">
          {alerts.slice(0, 3).map((alert) => (
            <article className="alert-item" key={alert.id}>
              <strong>{alert.event}</strong>
              <span>{alert.headline}</span>
              <small>{alert.severity} / {alert.urgency}</small>
            </article>
          ))}
        </div>
      ) : (
        <p>No active National Weather Service alerts for Stone Harbor right now.</p>
      )}
      <small>{conditions.scene.lightning.message} {isNativeApp() ? 'Check official warnings before heading out.' : 'Push subscribers receive warning and watch alerts while they are active.'}</small>
    </div>
  );
}

function Metric({ label, value, onActivate, targetLabel, tone }: { label: string; value: string; onActivate?: () => void; targetLabel?: string; tone?: "weather" | "wind" }) {
  const shortcutProps = onActivate
    ? {
        "aria-label": `Open ${targetLabel || label.toLowerCase()} from ${label.toLowerCase()} tile`,
        onClick: onActivate,
        onKeyDown: activateOnKeyboard(onActivate),
        role: "button",
        tabIndex: 0
      }
    : {};

  return (
    <div className={`metric-card${onActivate ? " shortcut-card" : ""}${tone ? ` tile-${tone}` : ""}`} {...shortcutProps}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function activateOnKeyboard(action: () => void) {
  return (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    action();
  };
}

function SunTime({ label, value }: { label: string; value: string }) {
  return (
    <div className="sun-time">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function ForecastCard({ period }: { period: ForecastPeriod }) {
  return (
    <article className="card forecast-card">
      <div>
        <strong>{period.label}</strong>
        <span>{period.summary}</span>
      </div>
      <div>
        <b>{period.temperatureF}°</b>
        <small>{period.windDirection} {period.windMph} mph</small>
      </div>
    </article>
  );
}

function DailyForecastCard({ period }: { period: DailyForecast }) {
  return (
    <article className="card forecast-card daily-forecast-card">
      <div>
        <strong>{period.label}</strong>
        <span>{period.summary}</span>
      </div>
      <div>
        <b>{period.highF}° / {period.lowF}°</b>
        <small>High / Low</small>
      </div>
    </article>
  );
}

function nextTideChipLabel(tide: BeachConditions["tide"]) {
  const availability = getTideAvailability(tide);
  if (availability === "unavailable") return "Tides unavailable";
  if (availability === "stale") return "Tide update delayed";
  const next = nextTideEvent(tide);
  if (!next) return "Tide checking";
  return `Next ${tideKindLabel(next.kind).toLowerCase()} ${shortTime(next.time)}`;
}

function tidePositionBadge(tide: BeachConditions["tide"]) {
  const availability = getTideAvailability(tide);
  if (availability === "unavailable") return "Unavailable";
  if (availability === "stale") return "Last update";
  if (!tide.current) return tideStateLabel(tide.state);
  return `${tideStateLabel(tide.state)} ${Math.round(tide.current.percent)}%`;
}

function tideSandFillPercent(tidePercent: number) {
  const clampedTide = Math.min(100, Math.max(0, tidePercent));
  return Math.round((80 - clampedTide * 0.6) * 10) / 10;
}

function tideMotionDirection(state: BeachConditions["tide"]["state"]) {
  if (state === "rising") return "rising";
  if (state === "falling") return "receding";
  return "still";
}

function tideStateLabel(state: BeachConditions["tide"]["state"]) {
  return state === "falling" ? "Receding" : sentenceCase(state);
}

function nextTideEvent(tide: BeachConditions["tide"]): TideEvent | undefined {
  return tide.next || [tide.nextHigh, tide.nextLow]
    .filter((event): event is TideEvent => Boolean(event))
    .sort((a, b) => Date.parse(a.time) - Date.parse(b.time))[0];
}

function getTideAvailability(tide: BeachConditions["tide"]): BeachConditions["tide"]["availability"] {
  return tide.availability || (tide.days.length ? "live" : "unavailable");
}

function ReactionSprite({ reaction }: { reaction: LiveReaction }) {
  const [isSitting, setIsSitting] = useState(false);
  const sitTimerRef = useRef<number | null>(null);
  const spriteRef = useRef<HTMLSpanElement | null>(null);
  const durationSeconds = Math.max(10, (Date.parse(reaction.expiresAt) - Date.parse(reaction.createdAt)) / 1000);
  const isInteractiveBasset = reaction.kind === "basset";
  const shootingStar = reaction.kind === "shootingStar" ? reaction.shootingStar ?? DEFAULT_SHOOTING_STAR : null;
  const style = {
    "--reaction-x": shootingStar && !reaction.shootingStar ? DEFAULT_SHOOTING_STAR.x : `${reaction.x}%`,
    "--reaction-y": shootingStar && !reaction.shootingStar ? DEFAULT_SHOOTING_STAR.y : `${reaction.y}%`,
    "--reaction-duration": `${durationSeconds}s`
  } as CSSProperties & Record<string, string>;

  if (shootingStar) {
    const { dx, dy } = shootingStar;
    const heading = (Math.atan2(dy, dx) * 180) / Math.PI;
    style["--shooting-star-early-x"] = `${Math.round(dx * 0.32)}px`;
    style["--shooting-star-early-y"] = `${Math.round(dy * 0.32)}px`;
    style["--shooting-star-mid-x"] = `${Math.round(dx * 0.64)}px`;
    style["--shooting-star-mid-y"] = `${Math.round(dy * 0.64)}px`;
    style["--shooting-star-end-x"] = `${dx}px`;
    style["--shooting-star-end-y"] = `${dy}px`;
    style["--shooting-star-head-angle"] = `${heading}deg`;
  }

  useEffect(() => {
    return () => {
      if (sitTimerRef.current !== null) {
        window.clearTimeout(sitTimerRef.current);
      }
    };
  }, []);

  const sitBasset = useCallback(() => {
    if (!isInteractiveBasset) return;
    if (sitTimerRef.current !== null) {
      window.clearTimeout(sitTimerRef.current);
    }
    setIsSitting(true);
    sitTimerRef.current = window.setTimeout(() => {
      setIsSitting(false);
      sitTimerRef.current = null;
    }, 3600);
  }, [isInteractiveBasset]);

  useEffect(() => {
    if (!isInteractiveBasset) return undefined;
    const handleDocumentPointerDown = (event: PointerEvent) => {
      const sprite = spriteRef.current;
      if (!sprite) return;
      const rect = sprite.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;
      if (!inside) return;
      event.preventDefault();
      event.stopPropagation();
      sitBasset();
    };
    document.addEventListener("pointerdown", handleDocumentPointerDown, true);
    return () => document.removeEventListener("pointerdown", handleDocumentPointerDown, true);
  }, [isInteractiveBasset, sitBasset]);

  const handleBassetKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    sitBasset();
  };

  return (
    <span
      aria-label={isInteractiveBasset ? "Basset hound, click to sit" : undefined}
      className={`reaction-sprite lane-${reaction.lane} kind-${reaction.kind}${isSitting ? " is-sitting" : ""}`}
      data-testid={`reaction-${reaction.kind}`}
      onClick={isInteractiveBasset ? sitBasset : undefined}
      onKeyDown={isInteractiveBasset ? handleBassetKeyDown : undefined}
      onPointerDown={isInteractiveBasset ? sitBasset : undefined}
      ref={spriteRef}
      role={isInteractiveBasset ? "button" : undefined}
      style={style}
      tabIndex={isInteractiveBasset ? 0 : undefined}
      title={reaction.label}
    >
      <ReactionGlyph kind={reaction.kind} glyph={reaction.glyph} />
    </span>
  );
}

function ReactionGlyph({ kind, glyph }: { kind: ReactionKind; glyph: string }) {
  if (kind === "sun") {
    return (
      <span className="mini-happy-sun" aria-hidden="true">
        <i className="mini-sun-eye left" />
        <i className="mini-sun-eye right" />
        <i className="mini-sun-bridge" />
        <i className="mini-sun-smile" />
      </span>
    );
  }
  if (kind === "moon") {
    return (
      <span className="mini-moon" aria-hidden="true">
        <i className="mini-moon-eye left" />
        <i className="mini-moon-eye right" />
        <i className="mini-moon-smile" />
      </span>
    );
  }
  if (kind === "shootingStar") {
    return <span className="shooting-star-sticker" aria-hidden="true"><i /><b /></span>;
  }
  if (kind === "seagull") {
    return <span className="seagull-sticker" aria-hidden="true"><i /><i /></span>;
  }
  if (kind === "lifeboat") {
    return <span className="lifeboat-sticker" aria-hidden="true"><i>SH</i></span>;
  }
  if (kind === "basset") {
    return (
      <span className="basset-sticker" aria-hidden="true">
        <i className="basset-shadow" />
        <i className="basset-body" />
        <i className="basset-patch" />
        <i className="basset-head" />
        <i className="basset-muzzle" />
        <i className="basset-ear" />
        <i className="basset-tail" />
        <i className="basset-leg front" />
        <i className="basset-leg back" />
        <i className="basset-paw front" />
        <i className="basset-paw back" />
      </span>
    );
  }
  if (kind === "rabbit") {
    return (
      <span className="rabbit-sticker" aria-hidden="true">
        <i className="rabbit-shadow" />
        <i className="rabbit-body" />
        <i className="rabbit-chest" />
        <i className="rabbit-head" />
        <i className="rabbit-ear left" />
        <i className="rabbit-ear right" />
        <i className="rabbit-eye" />
        <i className="rabbit-nose" />
        <i className="rabbit-whiskers" />
        <i className="rabbit-tail" />
        <i className="rabbit-leg front" />
        <i className="rabbit-leg hind" />
        <i className="rabbit-paw front" />
        <i className="rabbit-paw hind" />
      </span>
    );
  }
  if (kind === "biplane") {
    return (
      <span className="biplane-banner-sticker" aria-hidden="true">
        <span className="tow-banner">Support Stone Harbor Garden Club</span>
        <span className="banner-line" />
        <img className="old-plane-sticker biplane-image" src="/reactions/red-biplane.png" alt="" />
      </span>
    );
  }
  if (kind === "scotch") {
    return <span className="scotch-sticker" aria-hidden="true"><i /><b /></span>;
  }
  if (kind === "sandcastle") {
    return (
      <span className="sandcastle-sticker" aria-hidden="true">
        <i className="sandcastle-base" />
        <i className="sandcastle-tower left" />
        <i className="sandcastle-tower right" />
        <i className="sandcastle-keep" />
        <i className="sandcastle-gate" />
        <i className="sand-bucket" />
        <i className="sand-shovel" />
        <i className="sand-melt" />
      </span>
    );
  }
  if (kind === "hotAirBalloon") {
    return <span className="balloon-sticker" aria-hidden="true"><i /><b /></span>;
  }
  if (kind === "dolphin") {
    return <img className="dolphin-sticker dolphin-image" src="/reactions/dolphin.png" alt="" />;
  }
  if (kind === "sandpiper") {
    return (
      <span className="sandpiper-sticker" aria-hidden="true">
        <i className="sandpiper-head" />
        <i className="sandpiper-beak forward" />
        <i className="sandpiper-eye forward" />
        <i className="sandpiper-leg front" />
        <i className="sandpiper-leg back" />
      </span>
    );
  }
  if (kind === "crab") {
    return (
      <span className="crab-sticker" aria-hidden="true">
        <i className="crab-claw left" />
        <i className="crab-claw right" />
        <i className="crab-eye left" />
        <i className="crab-eye right" />
        <i className="crab-leg leg-one" />
        <i className="crab-leg leg-two" />
        <i className="crab-leg leg-three" />
        <i className="crab-leg leg-four" />
      </span>
    );
  }
  if (kind === "umbrella") {
    return <span className="umbrella-sticker" aria-hidden="true"><i /></span>;
  }
  if (kind === "shell") {
    return <span className="shell-sticker" aria-hidden="true"><i /></span>;
  }
  return <span aria-hidden="true">{glyph}</span>;
}

function shortTime(iso: string): string {
  return formatInStoneHarbor(iso, { hour: "numeric", minute: "2-digit" });
}

function shortDateTime(iso: string): string {
  return formatInStoneHarbor(iso, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function formatFeet(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function formatNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function sentenceCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function isGreenheadSuppressedByRain(conditions: BeachConditions): boolean {
  return conditions.scene.kind === "rainy" || conditions.scene.kind === "thunderstorm";
}

function shouldShowGreenheadInHero(conditions: BeachConditions): boolean {
  return !isGreenheadSuppressedByRain(conditions) && conditions.greenhead.label.toLowerCase() !== "quiet hours";
}

function readAlertPreferences(): AlertPreferences {
  if (typeof window === "undefined") return DEFAULT_ALERT_PREFERENCES;
  try {
    const raw = window.localStorage.getItem(ALERT_PREF_STORAGE_KEY);
    return normalizeAlertPreferences(raw ? JSON.parse(raw) as Partial<AlertPreferences> : undefined);
  } catch {
    return DEFAULT_ALERT_PREFERENCES;
  }
}

function storeAlertPreferences(preferences: AlertPreferences): void {
  try {
    window.localStorage.setItem(ALERT_PREF_STORAGE_KEY, JSON.stringify(preferences));
  } catch {
    // Local storage is a convenience; the server subscription remains the source for push delivery.
  }
}

function readAlertLocation(): AlertLocation | undefined {
  try {
    const raw = window.localStorage.getItem(ALERT_LOCATION_STORAGE_KEY);
    return raw ? JSON.parse(raw) as AlertLocation : undefined;
  } catch {
    return undefined;
  }
}

function storeAlertLocation(location: AlertLocation): void {
  try {
    window.localStorage.setItem(ALERT_LOCATION_STORAGE_KEY, JSON.stringify(location));
  } catch {
    // Location status is also included in the saved push subscription when alerts are enabled.
  }
}

async function getCurrentAlertLocation(): Promise<AlertLocation> {
  if (!("geolocation" in navigator)) {
    return unavailableAlertLocation("This browser cannot confirm the Stone Harbor alert area, so push alerts are paused.");
  }

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => resolve(alertLocationFromCoordinates(position.coords.latitude, position.coords.longitude)),
      () => resolve(unavailableAlertLocation("Location was not confirmed, so Stone Harbor push alerts are paused.")),
      { enableHighAccuracy: false, maximumAge: 10 * 60 * 1000, timeout: 8000 }
    );
  });
}

async function canRefreshLocationSilently(): Promise<boolean> {
  if (!("permissions" in navigator) || !("geolocation" in navigator)) return false;
  try {
    const status = await navigator.permissions.query({ name: "geolocation" as PermissionName });
    return status.state === "granted";
  } catch {
    return false;
  }
}

async function refreshExistingPushLocation(): Promise<void> {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
  if (!(await canRefreshLocationSilently())) return;
  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  if (!existing) return;
  const preferences = readAlertPreferences();
  const location = await getCurrentAlertLocation();
  storeAlertLocation(location);
  await savePushSubscription(existing, preferences, location);
}

function pushPreferenceMessage(preferences: AlertPreferences, existing: boolean, location?: AlertLocation): string {
  const selected = ALERT_OPTIONS.filter((option) => preferences[option.key]).map((option) => option.title.toLowerCase());
  if (!selected.length) return "Alert preferences saved; this device will not receive push alerts";
  if (!location?.inArea) return `${selected.join(", ")} alerts saved, but pushes are paused until this phone confirms it is within 10 miles of Stone Harbor.`;
  return `${existing ? "Updated" : "Enabled"} this local device for ${selected.join(", ")} alerts`;
}

function pruneReactions(items: LiveReaction[]): LiveReaction[] {
  const now = Date.now();
  return items.filter((reaction) => Date.parse(reaction.expiresAt) > now).slice(-MAX_VISIBLE_REACTIONS);
}

function normalizeReactions(items: LiveReaction[]): LiveReaction[] {
  const pruned = pruneReactions(items);
  const latestBiplaneIndex = findLatestReactionIndex(pruned, "biplane");
  if (latestBiplaneIndex < 0) return pruned;
  return pruned.filter((reaction, index) => reaction.kind !== "biplane" || index === latestBiplaneIndex);
}

function findLatestReactionIndex(items: LiveReaction[], kind: ReactionKind): number {
  let latestIndex = -1;
  let latestTime = -Infinity;
  items.forEach((reaction, index) => {
    if (reaction.kind !== kind) return;
    const createdAt = Date.parse(reaction.createdAt);
    if (createdAt >= latestTime) {
      latestTime = createdAt;
      latestIndex = index;
    }
  });
  return latestIndex;
}
